export type VerticalSlug = "hvac" | "plumbing" | "electrical" | "roofing";

export type Vertical = {
  slug: VerticalSlug;
  name: string;
  headline: string;
  description: string;
  painPoints: string[];
  outcomes: string[];
  modules: string[];
};

export const verticals: Record<VerticalSlug, Vertical> = {
  hvac: {
    slug: "hvac",
    name: "HVAC",
    headline: "Run HVAC sales, service, and maintenance on one platform",
    description:
      "From no-cool emergency calls to membership renewals and equipment history, ServiTrex keeps CSRs, dispatchers, and techs aligned — with AI voice covering the phone when your team cannot.",
    painPoints: [
      "Missed after-hours calls turning into competitor bookings",
      "Equipment history scattered across paper and inboxes",
      "Seasonal surge making dispatch guesswork",
      "Memberships sold in the field but never tracked cleanly",
    ],
    outcomes: [
      "AI agents book diagnostics and maintenance visits 24/7",
      "Property + equipment records travel with every job",
      "Dispatch balances skills, zones, and capacity in real time",
      "Maintenance agreements renew against actual service history",
    ],
    modules: [
      "AI voice intake",
      "Dispatch board",
      "Pricebook & estimates",
      "Memberships",
      "TrexField job flow",
      "Marketing automation",
    ],
  },
  plumbing: {
    slug: "plumbing",
    name: "Plumbing",
    headline: "Book, dispatch, and close plumbing work without the chaos",
    description:
      "Emergency leaks, drain work, water heaters, and remodel estimates all land in one system. ServiTrex connects the phone, office, and truck so nothing falls through the drain.",
    painPoints: [
      "High-urgency calls competing with scheduled installs",
      "Parts and truck stock surprises on water heater jobs",
      "Photo and warranty documentation lost after closeout",
      "Inconsistent quoting between CSRs and field teams",
    ],
    outcomes: [
      "Prioritized intake for emergencies vs. planned work",
      "Inventory visibility for common SKUs and truck stock",
      "Photo-rich job packets that stick to the customer record",
      "Shared pricebook so quotes stay consistent company-wide",
    ],
    modules: [
      "Call intake & AI voice",
      "CRM properties",
      "Inventory",
      "Estimates & invoices",
      "TrexField",
      "Customer portal",
    ],
  },
  electrical: {
    slug: "electrical",
    name: "Electrical",
    headline: "Coordinate electrical service, panels, and project work cleanly",
    description:
      "Service calls, panel upgrades, EV chargers, and generator installs need tight scheduling and clear documentation. ServiTrex keeps the office and field on the same job record from first call to final invoice.",
    painPoints: [
      "Permit and inspection steps tracked outside the job",
      "Skills-based assignment that still relies on tribal knowledge",
      "Change orders delayed because the office never saw field notes",
      "Follow-up for generators and EV work falling cold",
    ],
    outcomes: [
      "Job stages that include permitting and inspection checkpoints",
      "Skill tags that power smarter dispatch decisions",
      "Field notes and photos that update the office instantly",
      "Pipeline follow-ups for high-ticket electrical projects",
    ],
    modules: [
      "Dispatch & skills",
      "CRM pipeline",
      "Job documentation",
      "Estimates",
      "TrexField",
      "Marketing nurture",
    ],
  },
  roofing: {
    slug: "roofing",
    name: "Roofing",
    headline: "Win roofing jobs with tighter intake, estimates, and field proof",
    description:
      "Storm surge, insurance work, and residential reroofs demand fast response and airtight documentation. ServiTrex helps your team capture leads, schedule inspections, and close with confidence.",
    painPoints: [
      "Lead spikes after storms overwhelming the phones",
      "Inspection photos and measurements living in text threads",
      "Estimates slow to produce while competitors move first",
      "Production crews missing context from sales walkthroughs",
    ],
    outcomes: [
      "AI voice and CSRs capture storm leads without drop-off",
      "Inspection media attached to the property and opportunity",
      "Faster estimate packages from shared pricebooks",
      "Production handoff with the full sales and photo history",
    ],
    modules: [
      "AI voice & lead capture",
      "CRM opportunities",
      "Estimates",
      "Photo documentation",
      "Dispatch / production",
      "Invoicing",
    ],
  },
};

export const verticalList = Object.values(verticals);

export function isVerticalSlug(value: string): value is VerticalSlug {
  return value in verticals;
}
